import { useEffect, useState } from 'react';
import { MotionConfig } from 'framer-motion';
import { Outlet, useLocation } from 'react-router-dom';
import { GuildBar } from './GuildBar';
import { Sidebar } from './Sidebar';
import { TopHeader } from './TopHeader';
import { RouteContentBoundary } from './RouteContentBoundary';
import { getSettings, updateSettings } from '../lib/api';
import { useI18n } from '../contexts/I18nContext';
import { parseAppPath } from '../lib/routes';
import { useDashboardMotion } from './useDashboardMotion';
import './DashboardLayout.css';
import { DashboardSaveProvider } from './DashboardSaveBar';

export const DashboardLayout = () => {
  const { t } = useI18n();
  const [setupState, setSetupState] = useState(null);
  const [sidebarLocked, setSidebarLocked] = useState(false);
  const [selectedGuildId, setSelectedGuildId] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(max-width: 1023px)').matches;
  });
  const location = useLocation();
  const { wrapperRef, contentRef } = useDashboardMotion(location.pathname);
  const pathContext = parseAppPath(location.pathname);

  useEffect(() => {
    if (pathContext.scope !== 'server') return;
    const run = async () => {
      const tz = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || 'UTC';
      if (typeof tz !== 'string' || !tz) return;
      try {
        const settings = await getSettings();
        const current = typeof settings?.timezone === 'string' ? settings.timezone : '';
        const gid = (settings?.guild_id || '').toString();
        if (gid) setSelectedGuildId(gid);
        if (!current) {
          await updateSettings({ timezone: tz });
        }
      } catch (e) {
        return;
      }
    };
    run();
  }, [pathContext.scope]);

  useEffect(() => {
    const gid = String(pathContext.guildId || '');
    if (gid) setSelectedGuildId(gid);
  }, [pathContext.guildId]);

  useEffect(() => {
    const handler = (event) => {
      const next = event?.detail || null;
      setSetupState(next);
      setSidebarLocked(Boolean(next?.guild));
    };
    window.addEventListener('tns:guild-setup', handler);
    return () => window.removeEventListener('tns:guild-setup', handler);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const g = event?.detail?.guild || null;
      const gid = (g?.id || '').toString();
      if (gid) setSelectedGuildId(gid);
      if (!g) {
        setSidebarLocked(false);
        setSetupState(null);
        return;
      }
      if (g?.bot_in_guild === false) {
        setSidebarLocked(true);
        setSetupState((prev) => prev?.guild?.id === g.id ? prev : { guild: g, inviteUrl: '' });
        return;
      }
      setSidebarLocked(false);
      setSetupState(null);
    };
    window.addEventListener('tns:guild-selected', handler);
    return () => window.removeEventListener('tns:guild-selected', handler);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      const next = event?.detail || null;
      const gid = (next?.guild_id || '').toString();
      if (gid) setSelectedGuildId(gid);
    };
    window.addEventListener('tns:settings-updated', handler);
    return () => window.removeEventListener('tns:settings-updated', handler);
  }, []);

  useEffect(() => {
    if (sidebarLocked) setSidebarCollapsed(true);
  }, [sidebarLocked]);

  return (
    <MotionConfig reducedMotion="user">
      <div className="tns-dashboard-shell relative isolate h-screen bg-background flex overflow-hidden">
        <div className="tns-dashboard-backdrop pointer-events-none absolute inset-0" />
        <GuildBar
          sidebarCollapsed={sidebarCollapsed}
          sidebarLocked={sidebarLocked}
          onToggleSidebar={() => setSidebarCollapsed((prev) => !prev)}
        />
        {!sidebarLocked ? (
          <Sidebar collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} locked={sidebarLocked} />
        ) : null}
        <div className="relative z-10 flex-1 min-w-0 flex flex-col">
          <DashboardSaveProvider>
          <TopHeader sidebarLocked={sidebarLocked} sidebarCollapsed={sidebarCollapsed} />
          <main ref={wrapperRef} className="tns-dashboard-main flex-1 min-h-0 overflow-y-auto" tabIndex={-1}>
            <div ref={contentRef} className="tns-dashboard-content max-w-7xl mx-auto p-4 pb-32 sm:p-6 sm:pb-32 lg:p-8 lg:pb-32">
              {setupState?.guild ? (
                <div className="min-h-[60vh] flex items-center justify-center">
                  <div className="w-full max-w-lg rounded-xl border border-border bg-card p-6">
                    <div className="font-heading font-bold text-xl">
                      {t('هذا الخادم يتطلب الإعداد', 'This server requires setup')}
                    </div>
                    <div className="text-sm text-muted-foreground mt-2">
                      {t('أضف البوت إلى هذا الخادم للمتابعة.', 'Add the bot to this server to continue.')}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const url = setupState?.inviteUrl || '';
                        if (url) window.open(url, '_blank', 'noopener,noreferrer');
                      }}
                      disabled={!setupState?.inviteUrl}
                      className="mt-4 text-sm font-medium text-primary hover:underline disabled:opacity-60 disabled:hover:no-underline"
                      data-testid="guild-setup-inline-discord-link"
                    >
                      {t('الانتقال إلى ديسكورد', 'Go to Discord')}
                    </button>
                  </div>
                </div>
              ) : (
                <div
                  key={selectedGuildId || 'none'}
                  className="animate-in fade-in-0 slide-in-from-right-1 duration-300"
                >
                  <RouteContentBoundary>
                    <Outlet />
                  </RouteContentBoundary>
                </div>
              )}
            </div>
          </main>
          </DashboardSaveProvider>
        </div>
      </div>
    </MotionConfig>
  );
};
